<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>Support & Help</h2>
        <p>Resources for students and teachers using Space Coast STEM laptops</p>
      </div>
    </section>

    <section class="content-section">
      <div class="container">
        <h2 style="text-align: center;">Help Topics</h2>
        <div style="display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; margin: 2rem 0 3rem;">
          <button
            @click="currentSection = 'general'"
            class="btn btn-primary"
            :class="{ active: currentSection === 'general' }"
            style="cursor: pointer;">
            General
          </button>
          <button
            @click="currentSection = 'mint'"
            class="btn btn-secondary"
            :class="{ active: currentSection === 'mint' }"
            style="cursor: pointer;">
            Linux Mint
          </button>
          <button
            @click="currentSection = 'zorin'"
            class="btn btn-secondary"
            :class="{ active: currentSection === 'zorin' }"
            style="cursor: pointer;">
            Zorin OS
          </button>
        </div>

        <!-- General Support Section -->
        <div v-if="currentSection === 'general'">
          <h3 style="text-align: center; margin-bottom: 2rem;">General Information</h3>

          <div class="program-grid">
            <SupportCard
              icon="📋"
              title="Understanding Packing Slips"
              description="Learn about the performance ratings and specifications included with your laptop"
              action-text="details"
              @click="openModal('packingSlips')"
            />
          </div>

          <div class="info-box info" style="margin-top: 2rem;">
            <strong>Which Linux am I using?</strong> Our laptops now ship with <strong>Linux Mint (Xfce)</strong>.
            Laptops we handed out earlier run <strong>Zorin OS</strong> and are still fully supported &mdash; pick the
            tab above that matches your desktop. Not sure which one you have? Open the <strong>Menu</strong> and search
            for <strong>System Information</strong> (Linux Mint) or <strong>About</strong> (Zorin OS), or just email us
            and we'll help you figure it out.
          </div>

          <div class="help-box">
            <h3>Need More Help?</h3>
            <p>If you can't find what you're looking for or need additional assistance, please contact us:</p>
            <ul style="margin-top: 1rem;">
              <li><strong>Email:</strong> <a href="mailto:info@spacecoaststem.org">info@spacecoaststem.org</a></li>
              <li><strong>Subject Line:</strong> Include "Tech Support" in your email subject</li>
              <li><strong>Include:</strong> Your name, school, and a description of the issue</li>
            </ul>
          </div>
        </div>

        <!-- Linux Mint Support Section -->
        <div v-if="currentSection === 'mint'">
          <h3 style="text-align: center; margin-bottom: 2rem;">Common Tasks for Linux Mint (Xfce)</h3>

          <div class="program-grid">
            <SupportCard
              icon="🚀"
              title="Getting Started with Linux Mint"
              description="First time using your laptop? Start here for setup and basics"
              action-text="guide"
              @click="openModal('mintGettingStarted')"
            />

            <SupportCard
              icon="🐧"
              title="Why We Use Linux"
              description="Learn why we install Linux on all our refurbished machines"
              action-text="more"
              @click="openModal('whyLinux')"
            />

            <SupportCard
              icon="🔧"
              title="Setting the Username"
              description="Learn how to change your username and display name"
              action-text="steps"
              @click="openModal('mintUsername')"
            />

            <SupportCard
              icon="📦"
              title="Installing Software"
              description="Install new applications using the Software Manager"
              action-text="steps"
              @click="openModal('mintInstallSoftware')"
            />

            <SupportCard
              icon="⭐"
              title="Popular Packages"
              description="Recommended free software for students and creative work"
              action-text="browse"
              @click="openModal('popularPackages')"
            />

            <SupportCard
              icon="🎮"
              title="Gaming on Linux"
              description="Learn about playing and developing games on your laptop"
              action-text="explore"
              @click="openModal('gaming')"
            />

            <SupportCard
              icon="📚"
              title="Other Questions"
              description="Visit the official Linux Mint documentation for more guides and tutorials"
              action-text="linuxmint.com"
              @click="openModal('mintOtherQuestions')"
            />
          </div>

          <div class="help-box">
            <h3>Need More Help?</h3>
            <p>If you can't find what you're looking for or need additional assistance, please contact us:</p>
            <ul style="margin-top: 1rem;">
              <li><strong>Email:</strong> <a href="mailto:info@spacecoaststem.org">info@spacecoaststem.org</a></li>
              <li><strong>Subject Line:</strong> Include "Tech Support" in your email subject</li>
              <li><strong>Include:</strong> Your name, school, and a description of the issue</li>
            </ul>
          </div>
        </div>

        <!-- Zorin OS Support Section -->
        <div v-if="currentSection === 'zorin'">
          <h3 style="text-align: center; margin-bottom: 2rem;">Common Tasks for Zorin OS</h3>

          <div class="info-box info" style="margin-bottom: 2rem;">
            <strong>Still using Zorin OS?</strong> No problem. New Space Coast STEM laptops now ship with
            <strong>Linux Mint (Xfce)</strong>, but every Zorin OS laptop we handed out is still fully supported and
            these guides are kept up to date. There's no need to switch &mdash; if you'd like to move to Linux Mint
            anyway, just <a href="mailto:info@spacecoaststem.org">email us</a>.
          </div>

          <div class="program-grid">
            <SupportCard
              icon="🚀"
              title="Getting Started with Zorin OS"
              description="First time using your laptop? Start here for setup and basics"
              action-text="guide"
              @click="openModal('gettingStarted')"
            />

            <SupportCard
              icon="🐧"
              title="Why We Use Linux"
              description="Learn why we install Linux on all our refurbished machines"
              action-text="more"
              @click="openModal('whyLinux')"
            />

            <SupportCard
              icon="🔧"
              title="Setting the Username"
              description="Learn how to change your username and display name"
              action-text="steps"
              @click="openModal('username')"
            />

            <SupportCard
              icon="👪"
              title="Adding a User for Parental Controls"
              description="Create a new user account with parental controls"
              action-text="steps"
              @click="openModal('parentalControl')"
            />

            <SupportCard
              icon="📦"
              title="Installing Software"
              description="Install new applications using the Software Center"
              action-text="steps"
              @click="openModal('installSoftware')"
            />

            <SupportCard
              icon="⭐"
              title="Popular Packages"
              description="Recommended free software for students and creative work"
              action-text="browse"
              @click="openModal('popularPackages')"
            />

            <SupportCard
              icon="🎮"
              title="Gaming on Linux"
              description="Learn about playing and developing games on your laptop"
              action-text="explore"
              @click="openModal('gaming')"
            />

            <SupportCard
              icon="📚"
              title="Other Questions"
              description="Visit the official Zorin OS Help Center for more guides and tutorials"
              action-text="help.zorin.com"
              @click="openModal('otherQuestions')"
            />
          </div>

          <div class="help-box">
            <h3>Need More Help?</h3>
            <p>If you can't find what you're looking for or need additional assistance, please contact us:</p>
            <ul style="margin-top: 1rem;">
              <li><strong>Email:</strong> <a href="mailto:info@spacecoaststem.org">info@spacecoaststem.org</a></li>
              <li><strong>Subject Line:</strong> Include "Tech Support" in your email subject</li>
              <li><strong>Include:</strong> Your name, school, and a description of the issue</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Modal System -->
    <Teleport to="body">
      <div v-if="activeModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-content">
          <button class="modal-close" @click="closeModal">&times;</button>

          <!-- Packing Slips Modal -->
          <div v-if="activeModal === 'packingSlips'">
            <h2>Understanding Your Laptop's Packing Slip</h2>
            <p style="margin-top: 1rem;">Every laptop from Space Coast STEM comes with a packing slip that includes important information about your device.</p>

            <div style="margin-top: 2rem; text-align: center;">
              <img src="/assets/img/packing_slip.png" alt="Example Packing Slip" style="max-width: 100%; border: 2px solid #ddd; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
              <p style="margin-top: 0.5rem; font-size: 0.9rem; color: #666; font-style: italic;">Example of a laptop packing slip</p>
            </div>

            <div style="margin-top: 2rem;">
              <h3>What's on the Packing Slip?</h3>
              <ul style="margin-top: 1rem;">
                <li><strong>Laptop Model:</strong> The brand and model name prominently displayed at the top</li>
                <li><strong>Performance Rating:</strong> A 1-10 score with color-coded badge (yellow/orange for 1-3, green for 4-7, blue for 8-10)</li>
                <li><strong>Screen Size:</strong> Display diagonal measurement in inches</li>
                <li><strong>CPU:</strong> Processor model (e.g., Intel Core i5, AMD Ryzen)</li>
                <li><strong>GPU:</strong> Graphics card (if present - important for creative work and gaming)</li>
                <li><strong>RAM:</strong> Memory capacity (4GB, 8GB, 16GB, etc.)</li>
                <li><strong>Storage:</strong> Hard drive or SSD capacity</li>
                <li><strong>Operating System:</strong> Usually Linux (Linux Mint on our current laptops, Zorin OS on older ones) or Windows</li>
                <li><strong>Recommended For:</strong> List of suitable activities based on the rating and specs</li>
                <li><strong>Special Notes:</strong> Any unique features or considerations</li>
              </ul>

              <h3 style="margin-top: 2rem;">Performance Rating Scale</h3>
              <p style="margin-top: 1rem;">We rate each laptop on a 1-10 scale to help you understand what it can do:</p>

              <p>We have minimum requirements for the equipment we distribute, so a low rating does not mean that it is not a capable machine. It is just not meant for handling more demanding tasks. All of our laptops are suitable for basic educational activities.</p>

              <div class="rating-visual"></div>

              <div class="rating-box basic">
                <h4>⭐ Rating 1-3: Basic Performance</h4>
                <p><strong>Best for:</strong> Basic homework, web browsing, email, word processing, and simple research</p>
                <p><strong>Examples:</strong> Writing essays, browsing educational websites, checking email, watching videos</p>
              </div>

              <div class="rating-box standard">
                <h4>⭐⭐ Rating 4-7: Standard Performance</h4>
                <p><strong>Best for:</strong> More intense tasks, multi-tasking, presentations, light photo editing, and educational software</p>
                <p><strong>Examples:</strong> Running multiple programs at once, creating presentations, basic coding, light image editing, video conferencing</p>
              </div>

              <div class="rating-box high">
                <h4>⭐⭐⭐ Rating 8-10: High Performance</h4>
                <p><strong>Best for:</strong> Demanding applications, creative work, programming, video editing, and specialized tasks</p>
                <p><strong>Examples:</strong> Advanced coding projects, video/photo editing, 3D modeling, machine learning experiments, creative tools like GIMP or Blender</p>
                <p><em>Note: Some laptops in this range include dedicated GPUs for graphics-intensive work</em></p>
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Mint: Getting Started Modal -->
          <div v-if="activeModal === 'mintGettingStarted'">
            <h2>Getting Started with Linux Mint</h2>
            <p style="margin-top: 1rem;">Welcome to your new Space Coast STEM laptop! Your laptop runs <strong>Linux Mint</strong> with the <strong>Xfce</strong> desktop &mdash; a fast, lightweight desktop that keeps older hardware feeling quick. This guide will help you get started.</p>

            <div style="margin-top: 2rem;">
              <h3>Default Login Credentials</h3>
              <div class="info-box warning">
                <p style="margin: 0; font-size: 1.1rem;"><strong>Username:</strong> <code>user</code></p>
                <p style="margin: 0.5rem 0 0 0; font-size: 1.1rem;"><strong>Password:</strong> <code>password</code></p>
              </div>
              <p style="margin-top: 1rem; font-style: italic; color: #666;"><strong>Important:</strong> For security, we recommend changing your username and password after your first login.</p>

              <h3 style="margin-top: 2rem;">Your Linux Mint Xfce Desktop</h3>
              <p style="margin-top: 1rem;">Everything lives on the panel along the bottom of the screen:</p>
              <ul style="margin-top: 1rem;">
                <li><strong>Menu (bottom-left):</strong> Click the Linux Mint logo to open the menu. Start typing to search for any app or setting &mdash; this is the fastest way to find anything.</li>
                <li><strong>Favorites &amp; categories:</strong> The menu groups apps into categories (Office, Graphics, Internet, Administration), with your favorites pinned on the left.</li>
                <li><strong>System tray (bottom-right):</strong> Network, sound, battery, and the <strong>Update Manager</strong> shield icon.</li>
                <li><strong>Files:</strong> The file manager (Thunar) handles your Documents, Downloads, and USB drives.</li>
                <li><strong>Settings:</strong> Open <strong>Menu &rarr; Settings &rarr; Settings Manager</strong> to customize anything about your system.</li>
              </ul>

              <h3 style="margin-top: 2rem;">First Things to Do</h3>
              <ol style="margin-top: 1rem;">
                <li><strong>Connect to Wi-Fi:</strong> Click the network icon in the system tray and pick your network.</li>
                <li><strong>Install updates:</strong> Click the shield icon in the system tray to open <strong>Update Manager</strong>, then click <strong>Install Updates</strong>. Do this first &mdash; it keeps your laptop secure.</li>
                <li><strong>Change your password:</strong> See the <em>Setting the Username</em> guide on the previous page.</li>
                <li><strong>Explore the Welcome Screen:</strong> Linux Mint's Welcome Screen has a guided first-steps tour. Find it in the menu by searching for "Welcome".</li>
              </ol>

              <h3 style="margin-top: 2rem;">Tutorials &amp; Official Guides</h3>
              <p style="margin-top: 1rem;">Linux Mint publishes a complete, beginner-friendly user guide. It covers the desktop, software, updates, and troubleshooting:</p>
              <div style="margin-top: 1.5rem; text-align: center;">
                <button @click="openExternal('https://linuxmint.com/documentation.php')" class="btn btn-primary" style="font-size: 1.1rem;">
                  Linux Mint User Guide
                  <span style="margin-left: 0.5rem;">&rarr;</span>
                </button>
              </div>

              <div class="info-box info" style="margin-top: 2rem;">
                <strong>Need Help?</strong> If you have questions or run into issues, email us at <a href="mailto:info@spacecoaststem.org">info@spacecoaststem.org</a> with "Tech Support" in the subject line.
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Mint: Username Modal -->
          <div v-if="activeModal === 'mintUsername'">
            <h2>Setting the Username</h2>
            <p style="margin-top: 1rem;">Follow these steps to change your display name and password in Linux Mint (Xfce):</p>

            <div style="margin-top: 2rem;">
              <h3>Step 1: Open Users and Groups</h3>
              <ol>
                <li>Click the <strong>Menu</strong> button in the bottom-left corner of your screen</li>
                <li>Type <strong>Users</strong> in the search box</li>
                <li>Click on <strong>Users and Groups</strong> (it also lives under the <strong>Administration</strong> category)</li>
                <li>Enter your password when prompted &mdash; this tool needs administrator access</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 2: Select Your Account</h3>
              <ol>
                <li>You'll see a list of accounts on the laptop</li>
                <li>Click on your own account to select it</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 3: Change Your Display Name</h3>
              <ol>
                <li>Click on the <strong>name</strong> shown next to the account picture</li>
                <li>Type your new display name (this is what appears on the login screen)</li>
                <li>Press <strong>Enter</strong> to save</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 4: Change Your Password</h3>
              <ol>
                <li>Click on the <strong>Password</strong> field for your account</li>
                <li>Enter your new password twice</li>
                <li>Confirm to save the change</li>
              </ol>

              <div class="info-box success">
                <strong>Tip:</strong> Pick a password you'll remember. Unlike a school account, there's nobody who can reset a forgotten Linux password for you remotely &mdash; you'd have to bring the laptop to us.
              </div>

              <div class="info-box warning">
                <strong>Note:</strong> To change your actual username (login name), you may need administrator assistance. Contact your teacher or email us for help with this advanced task.
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Mint: Install Software Modal -->
          <div v-if="activeModal === 'mintInstallSoftware'">
            <h2>Installing Software</h2>
            <p style="margin-top: 1rem;">Learn how to install new applications on your Linux Mint laptop:</p>

            <div style="margin-top: 2rem;">
              <h3>Method 1: Using the Software Manager (Recommended)</h3>

              <h4 style="margin-top: 1.5rem;">Step 1: Open Software Manager</h4>
              <ol>
                <li>Click on the <strong>Menu</strong> button</li>
                <li>Search for and click on <strong>Software Manager</strong></li>
              </ol>

              <h4 style="margin-top: 1.5rem;">Step 2: Search for Software</h4>
              <ol>
                <li>Use the <strong>Search</strong> bar at the top</li>
                <li>Type the name of the application you want (e.g., "GIMP", "VLC", "LibreOffice")</li>
                <li>Browse through the results</li>
              </ol>

              <h4 style="margin-top: 1.5rem;">Step 3: Install the Application</h4>
              <ol>
                <li>Click on the application you want to install</li>
                <li>Read the description and check the reviews</li>
                <li>Click the <strong>Install</strong> button</li>
                <li>Enter your password when prompted</li>
                <li>Wait for the installation to complete</li>
              </ol>

              <h4 style="margin-top: 1.5rem;">Step 4: Launch Your New Application</h4>
              <ol>
                <li>Click the <strong>Menu</strong> button</li>
                <li>Find your newly installed application (or just search for it)</li>
                <li>Click to launch it</li>
              </ol>

              <div class="info-box info" style="margin-top: 1.5rem;">
                <strong>Flatpak apps:</strong> Linux Mint's Software Manager also includes Flatpak apps out of the box. Some programs are listed twice &mdash; once from the Linux Mint repositories and once as a Flatpak. Either works; the repository version is usually smaller.
              </div>

              <h3 style="margin-top: 2rem;">Method 2: Using the Terminal (Advanced)</h3>
              <p style="margin-top: 1rem;">Once you know a package's name, the terminal is the quickest way to install it. Open <strong>Terminal</strong> from the menu and run:</p>
              <code style="display: block; margin-top: 0.5rem; padding: 0.75rem; background: rgba(0, 0, 0, 0.1); border-radius: 4px; font-size: 0.95rem;">sudo apt update<br>sudo apt install gimp</code>
              <p style="margin-top: 0.5rem; font-size: 0.9rem; font-style: italic;">Replace <code>gimp</code> with whichever package you want. You'll be asked for your password, then to confirm with <code>y</code>.</p>

              <h3 style="margin-top: 2rem;">Keeping Software Updated</h3>
              <p style="margin-top: 1rem;">Click the <strong>shield icon</strong> in the system tray (bottom-right) to open <strong>Update Manager</strong>, then click <strong>Install Updates</strong>. This updates your whole system and every app you installed through it &mdash; there's no need to update programs one by one.</p>

              <h3 style="margin-top: 2rem;">Popular Free Applications for Students</h3>
              <ul style="margin-top: 1rem;">
                <li><strong>LibreOffice:</strong> Word processor, spreadsheet, and presentation software (already installed)</li>
                <li><strong>GIMP:</strong> Image editing (like Photoshop)</li>
                <li><strong>Inkscape:</strong> Vector graphics editor</li>
                <li><strong>Blender:</strong> 3D modeling and animation</li>
                <li><strong>Audacity:</strong> Audio recording and editing</li>
                <li><strong>VLC Media Player:</strong> Play videos and music</li>
                <li><strong>Visual Studio Code:</strong> Code editor for programming</li>
              </ul>

              <div class="info-box warning">
                <strong>Note:</strong> You need administrator privileges to install software. If you're using a student account, ask your parent or teacher for assistance.
              </div>

              <div class="info-box success">
                <strong>Safety Tip:</strong> Only install software from the official Software Manager to ensure it's safe and virus-free!
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Mint: Other Questions Modal -->
          <div v-if="activeModal === 'mintOtherQuestions'">
            <h2>Additional Resources</h2>
            <p style="margin-top: 1rem;">Can't find what you're looking for? We have additional resources available!</p>

            <div style="margin-top: 2rem;">
              <h3>🌐 Official Linux Mint Documentation</h3>
              <p style="margin-top: 1rem;">The official Linux Mint User Guide provides comprehensive guides, tutorials, and troubleshooting help for all aspects of using Linux Mint.</p>

              <div class="info-box info" style="margin-top: 1.5rem;">
                <strong>Topics covered include:</strong>
                <ul style="margin-top: 0.5rem; margin-bottom: 0;">
                  <li>Getting started with Linux Mint</li>
                  <li>Customizing your desktop</li>
                  <li>Installing and managing software</li>
                  <li>System settings and preferences</li>
                  <li>Troubleshooting common issues</li>
                  <li>Advanced features and tips</li>
                </ul>
              </div>

              <div style="margin-top: 1.5rem; text-align: center; display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap;">
                <button @click="openExternal('https://linuxmint.com/documentation.php')" class="btn btn-primary" style="font-size: 1.1rem;">
                  Linux Mint User Guide
                  <span style="margin-left: 0.5rem;">&rarr;</span>
                </button>
                <button @click="openExternal('https://forums.linuxmint.com/')" class="btn btn-secondary" style="font-size: 1.1rem;">
                  Community Forums
                  <span style="margin-left: 0.5rem;">&rarr;</span>
                </button>
              </div>

              <p style="margin-top: 1.5rem;">The <strong>community forums</strong> are a friendly place to search for an answer or ask a question &mdash; chances are someone has already hit the same issue.</p>

              <h3 style="margin-top: 2.5rem;">📧 Contact Space Coast STEM</h3>
              <p style="margin-top: 1rem;">If you still need help or have questions specific to your Space Coast STEM laptop:</p>

              <div class="info-box warning" style="margin-top: 1rem;">
                <ul style="margin: 0;">
                  <li><strong>Email:</strong> <a href="mailto:info@spacecoaststem.org" style="color: inherit; text-decoration: underline;">info@spacecoaststem.org</a></li>
                  <li><strong>Subject Line:</strong> Include "Tech Support" in your email subject</li>
                  <li><strong>Include:</strong> Your name, school, and a description of the issue</li>
                </ul>
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Getting Started Modal -->
          <div v-if="activeModal === 'gettingStarted'">
            <h2>Getting Started with Zorin OS</h2>
            <p style="margin-top: 1rem;">Welcome to your new Space Coast STEM laptop! This guide will help you get started with Zorin OS.</p>

            <div style="margin-top: 2rem;">
              <h3>Default Login Credentials</h3>
              <div class="info-box warning">
                <p style="margin: 0; font-size: 1.1rem;"><strong>Username:</strong> <code>user</code></p>
                <p style="margin: 0.5rem 0 0 0; font-size: 1.1rem;"><strong>Password:</strong> <code>password</code></p>
              </div>
              <p style="margin-top: 1rem; font-style: italic; color: #666;"><strong>Important:</strong> For security, we recommend changing your username and password after your first login.</p>

              <h3 style="margin-top: 2rem;">Video Tutorial: Zorin OS Basics</h3>
              <p style="margin-top: 1rem;">Watch this comprehensive video tutorial to learn the basics of using Zorin OS:</p>

              <div style="margin-top: 1.5rem; position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 8px;">
                <iframe
                  style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
                  src="https://www.youtube.com/embed/0K43Bxg8MLU?start=1415&si=dDdS7-QabhwWCO16"
                  title="Getting Started with Zorin OS"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen>
                </iframe>
              </div>

              <h3 style="margin-top: 2rem;">Quick Start Tips</h3>
              <ul style="margin-top: 1rem;">
                <li><strong>Application Menu:</strong> Click the grid icon in the bottom-left corner to access all your apps</li>
                <li><strong>Settings:</strong> Click the gear icon to customize your system</li>
                <li><strong>Files:</strong> Find all your documents, downloads, and files in the file manager</li>
                <li><strong>Software Center:</strong> Install new applications from the Software app</li>
                <li><strong>Updates:</strong> Keep your system secure by installing updates when prompted</li>
              </ul>

              <div class="info-box info">
                <strong>Need Help?</strong> If you have questions or run into issues, email us at <a href="mailto:info@spacecoaststem.org">info@spacecoaststem.org</a> with "Tech Support" in the subject line.
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Username Modal -->
          <div v-if="activeModal === 'username'">
            <h2>Setting the Username</h2>
            <p style="margin-top: 1rem;">Follow these steps to change your username and display name in Zorin OS:</p>

            <div style="margin-top: 2rem;">
              <h3>Step 1: Open System Settings</h3>
              <ol>
                <li>Click on the <strong>Menu</strong> button in the bottom-left corner of your screen</li>
                <li>Click on <strong>Settings</strong> (the gear icon)</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 2: Navigate to Users</h3>
              <ol>
                <li>In the Settings window, click on <strong>Users</strong> in the left sidebar</li>
                <li>You'll see your current user account displayed</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 3: Unlock Settings</h3>
              <ol>
                <li>Click the <strong>Unlock</strong> button in the top-right corner</li>
                <li>Enter your password when prompted</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 4: Change Your Name</h3>
              <ol>
                <li>Click on your <strong>Full Name</strong> field</li>
                <li>Type your new display name (this is what appears on the login screen)</li>
                <li>Press <strong>Enter</strong> to save</li>
              </ol>

              <div class="info-box warning">
                <strong>Note:</strong> To change your actual username (login name), you may need administrator assistance. Contact your teacher or email us for help with this advanced task.
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Parental Control Modal -->
          <div v-if="activeModal === 'parentalControl'">
            <h2>Adding a User for Parental Controls</h2>
            <p style="margin-top: 1rem;">Create a new user account with restricted access and parental controls:</p>

            <div style="margin-top: 2rem;">
              <h3>Step 1: Open System Settings</h3>
              <ol>
                <li>Click on the <strong>Menu</strong> button in the bottom-left corner</li>
                <li>Open <strong>Settings</strong></li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 2: Go to Users</h3>
              <ol>
                <li>Click on <strong>Users</strong> in the left sidebar</li>
                <li>Click the <strong>Unlock</strong> button and enter your password</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 3: Add New User</h3>
              <ol>
                <li>Click the <strong>Add User</strong> button (+ icon)</li>
                <li>Choose <strong>Standard</strong> account type (not Administrator)</li>
                <li>Enter the <strong>Full Name</strong> for the new user</li>
                <li>Enter a <strong>Username</strong> (no spaces, lowercase)</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 4: Set Password</h3>
              <ol>
                <li>Choose <strong>"Set a password now"</strong></li>
                <li>Enter a secure password twice</li>
                <li>Click <strong>Add</strong> to create the account</li>
              </ol>

              <h3 style="margin-top: 2rem;">Step 5: Configure Parental Controls</h3>
              <ol>
                <li>Select the newly created user account</li>
                <li>Click on <strong>Parental Controls</strong> (if available)</li>
                <li>Set restrictions for application usage, web browsing, and time limits</li>
              </ol>

              <div class="info-box info">
                <strong>Tip:</strong> Standard user accounts cannot install software or change system settings, providing a safer environment for younger users.
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Install Software Modal -->
          <div v-if="activeModal === 'installSoftware'">
            <h2>Installing Software</h2>
            <p style="margin-top: 1rem;">Learn how to install new applications on your Zorin OS laptop:</p>

            <div style="margin-top: 2rem;">
              <h3>Method 1: Using the Software Center (Recommended)</h3>

              <h4 style="margin-top: 1.5rem;">Step 1: Open Software Center</h4>
              <ol>
                <li>Click on the <strong>Menu</strong> button</li>
                <li>Search for and click on <strong>Software</strong></li>
              </ol>

              <h4 style="margin-top: 1.5rem;">Step 2: Search for Software</h4>
              <ol>
                <li>Use the <strong>Search</strong> bar at the top</li>
                <li>Type the name of the application you want (e.g., "GIMP", "VLC", "LibreOffice")</li>
                <li>Browse through the results</li>
              </ol>

              <h4 style="margin-top: 1.5rem;">Step 3: Install the Application</h4>
              <ol>
                <li>Click on the application you want to install</li>
                <li>Read the description and check the reviews</li>
                <li>Click the <strong>Install</strong> button</li>
                <li>Enter your password when prompted</li>
                <li>Wait for the installation to complete</li>
              </ol>

              <h4 style="margin-top: 1.5rem;">Step 4: Launch Your New Application</h4>
              <ol>
                <li>Click the <strong>Menu</strong> button</li>
                <li>Find your newly installed application</li>
                <li>Click to launch it</li>
              </ol>

              <h3 style="margin-top: 2rem;">Popular Free Applications for Students</h3>
              <ul style="margin-top: 1rem;">
                <li><strong>LibreOffice:</strong> Word processor, spreadsheet, and presentation software</li>
                <li><strong>GIMP:</strong> Image editing (like Photoshop)</li>
                <li><strong>Inkscape:</strong> Vector graphics editor</li>
                <li><strong>Blender:</strong> 3D modeling and animation</li>
                <li><strong>Audacity:</strong> Audio recording and editing</li>
                <li><strong>VLC Media Player:</strong> Play videos and music</li>
                <li><strong>Visual Studio Code:</strong> Code editor for programming</li>
              </ul>

              <div class="info-box warning">
                <strong>Note:</strong> You need administrator privileges to install software. If you're using a student account, ask your parent or teacher for assistance.
              </div>

              <div class="info-box success">
                <strong>Safety Tip:</strong> Only install software from the official Software Center to ensure it's safe and virus-free!
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Popular Packages Modal -->
          <div v-if="activeModal === 'popularPackages'">
            <h2>Popular Free Software for Students</h2>
            <p style="margin-top: 1rem;">Discover powerful free and open-source applications perfect for education, creativity, and productivity. All of these can be installed from your app store &mdash; the <strong>Software Manager</strong> on Linux Mint, or the <strong>Software Center</strong> on Zorin OS!</p>

            <div style="margin-top: 2rem;">
              <h3>🎨 Creative & Design</h3>
              <div class="package-grid">
                <div class="package-card" @click="openExternal('https://www.gimp.org/')">
                  <img src="/assets/img/logos/gimp.png" alt="GIMP" class="package-logo">
                  <h4>GIMP</h4>
                  <p>Professional photo editing and image manipulation (like Photoshop)</p>
                </div>

                <div class="package-card" @click="openExternal('https://inkscape.org/')">
                  <img src="/assets/img/logos/inkscape.png" alt="Inkscape" class="package-logo">
                  <h4>Inkscape</h4>
                  <p>Vector graphics editor for logos, illustrations, and design</p>
                </div>

                <div class="package-card" @click="openExternal('https://www.blender.org/')">
                  <img src="/assets/img/logos/blender.png" alt="Blender" class="package-logo">
                  <h4>Blender</h4>
                  <p>3D modeling, animation, rendering, and game development</p>
                </div>

                <div class="package-card" @click="openExternal('https://krita.org/')">
                  <img src="/assets/img/logos/krita.png" alt="Krita" class="package-logo">
                  <h4>Krita</h4>
                  <p>Digital painting and illustration for artists</p>
                </div>
              </div>

              <h3 style="margin-top: 2rem;">📝 Office & Productivity</h3>
              <div class="package-grid">
                <div class="package-card" @click="openExternal('https://www.libreoffice.org/')">
                  <img src="/assets/img/logos/libreoffice.png" alt="LibreOffice" class="package-logo">
                  <h4>LibreOffice</h4>
                  <p>Complete office suite - Writer, Calc, Impress, and more</p>
                </div>

                <div class="package-card" @click="openExternal('https://obsidian.md/')">
                  <img src="/assets/img/logos/obsidian.png" alt="Obsidian" class="package-logo">
                  <h4>Obsidian</h4>
                  <p>Knowledge base and note-taking with markdown support</p>
                </div>
              </div>

              <h3 style="margin-top: 2rem;">💻 Programming & Development</h3>
              <div class="package-grid">
                <div class="package-card" @click="openExternal('https://code.visualstudio.com/')">
                  <img src="/assets/img/logos/vscode.png" alt="VS Code" class="package-logo">
                  <h4>Visual Studio Code</h4>
                  <p>Powerful code editor with extensions for any language</p>
                </div>

                <div class="package-card" @click="openExternal('https://www.python.org/')">
                  <img src="/assets/img/logos/python.png" alt="Python" class="package-logo">
                  <h4>Python</h4>
                  <p>Popular programming language for beginners and professionals</p>
                </div>

                <div class="package-card" @click="openExternal('https://scratch.mit.edu/')">
                  <img src="/assets/img/logos/scratch.png" alt="Scratch" class="package-logo">
                  <h4>Scratch</h4>
                  <p>Visual programming language perfect for learning to code</p>
                </div>
              </div>

              <h3 style="margin-top: 2rem;">⚙️ Engineering & CAD</h3>
              <div class="package-grid">
                <div class="package-card" @click="openExternal('https://www.freecad.org/')">
                  <img src="/assets/img/logos/freecad.png" alt="FreeCAD" class="package-logo">
                  <h4>FreeCAD</h4>
                  <p>Parametric 3D modeling for mechanical engineering and design</p>
                </div>

                <div class="package-card" @click="openExternal('https://www.kicad.org/')">
                  <img src="/assets/img/logos/kicad.png" alt="KiCad" class="package-logo">
                  <h4>KiCad</h4>
                  <p>Electronics design automation for PCB design and schematics</p>
                </div>

                <div class="package-card" @click="openExternal('https://fritzing.org/')">
                  <img src="/assets/img/logos/fritzing.png" alt="Fritzing" class="package-logo">
                  <h4>Fritzing</h4>
                  <p>Electronics prototyping and circuit design for Arduino projects</p>
                </div>
              </div>

              <h3 style="margin-top: 2rem;">🎬 Media & Entertainment</h3>
              <div class="package-grid">
                <div class="package-card" @click="openExternal('https://www.videolan.org/vlc/')">
                  <img src="/assets/img/logos/vlc.png" alt="VLC" class="package-logo">
                  <h4>VLC Media Player</h4>
                  <p>Play any video or audio format</p>
                </div>

                <div class="package-card" @click="openExternal('https://www.audacityteam.org/')">
                  <img src="/assets/img/logos/audacity.png" alt="Audacity" class="package-logo">
                  <h4>Audacity</h4>
                  <p>Record and edit audio files</p>
                </div>

                <div class="package-card" @click="openExternal('https://obsproject.com/')">
                  <img src="/assets/img/logos/obs.png" alt="OBS Studio" class="package-logo">
                  <h4>OBS Studio</h4>
                  <p>Screen recording and live streaming software</p>
                </div>

                <div class="package-card" @click="openExternal('https://kdenlive.org/')">
                  <img src="/assets/img/logos/kdenlive.png" alt="Kdenlive" class="package-logo">
                  <h4>Kdenlive</h4>
                  <p>Video editing with professional features</p>
                </div>
              </div>

              <div class="info-box info" style="margin-top: 2rem;">
                <strong>How to Install:</strong> Open the <strong>Software Manager</strong> (Linux Mint) or <strong>Software Center</strong> (Zorin OS) from your menu, search for the application name, and click <strong>Install</strong>. Most of these apps are completely free and open-source!
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Why Linux Modal -->
          <div v-if="activeModal === 'whyLinux'">
            <h2>Why We Choose Linux</h2>
            <p style="margin-top: 1rem;">At Space Coast STEM, we install Linux (specifically <a href="https://linuxmint.com/" target="_blank" rel="noopener noreferrer" style="color: var(--secondary-color); text-decoration: underline;">Linux Mint</a> with the Xfce desktop) on all refurbished machines unless there are special circumstances or requests. Here's why this decision benefits students, schools, and the environment.</p>

            <div style="margin-top: 2rem;">
              <h3>Why Linux?</h3>

              <div class="benefit-item">
                <h4>⚡ Superior Performance</h4>
                <p>Linux is engineered for efficiency. While Windows and macOS have become increasingly resource-heavy, Linux delivers fast, responsive performance on both new and older hardware. Students get a modern operating system that runs smoothly without the distraction of unwanted ads and newsfeeds.</p>
              </div>

              <div class="benefit-item">
                <h4>♻️ Sustainable Technology</h4>
                <p>Linux's efficient design means hardware stays capable and relevant longer. Machines that might be considered "too old" for the latest Windows or macOS continue to run modern Linux perfectly, reducing e-waste while giving students fully functional, contemporary computers.</p>
              </div>

              <div class="benefit-item">
                <h4>🆓 Zero Licensing Costs</h4>
                <p>Linux is completely free and open source. We don't have to pay for operating system licenses when refurbishing laptops, which means more resources can go directly toward helping students rather than software licensing fees.</p>
              </div>

              <div class="benefit-item">
                <h4>🔧 Professional-Grade Software Ecosystem</h4>
                <p>Linux provides access to thousands of powerful, professional applications—all free. From development tools to creative suites to productivity software, students get the same industry-standard tools used by developers, engineers, and professionals worldwide, without any licensing costs.</p>
              </div>

              <div class="benefit-item">
                <h4>💼 Industry-Standard Technology</h4>
                <p>Linux powers the modern world. It runs the majority of web servers, all supercomputers, cloud infrastructure, and smartphones (Android). By using Linux, students gain hands-on experience with the same operating system that drives global technology, from NASA to Google to Amazon.</p>
              </div>

              <div class="benefit-item">
                <h4>🎓 Real-World Technical Experience</h4>
                <p>Using Linux teaches students problem-solving, technical literacy, and how professional computing environments work. These are transferable skills that benefit any STEM career path, from software engineering to data science to cybersecurity.</p>
              </div>

              <h3 style="margin-top: 2rem;">Why Linux Mint (Xfce) Specifically?</h3>
              <p style="margin-top: 1rem;">We install <strong>Linux Mint</strong> with the <strong>Xfce</strong> desktop on the laptops we refurbish today:</p>
              <ul style="margin-top: 1rem;">
                <li><strong>Familiar, Intuitive Interface:</strong> A traditional menu and taskbar layout that anyone coming from Windows can use immediately</li>
                <li><strong>Lightweight and Fast:</strong> Xfce uses very little memory, so the older laptops we refurbish stay genuinely quick</li>
                <li><strong>Rock-Solid and Well Supported:</strong> Built on Ubuntu LTS with years of security updates, and one of the largest beginner communities in Linux</li>
                <li><strong>Simple, Safe Software Installs:</strong> The Software Manager bundles repository and Flatpak apps in one place, and Update Manager keeps everything current in one click</li>
                <li><strong>Extensive Software Library:</strong> Access to thousands of free, professional applications</li>
                <li><strong>Excellent Documentation:</strong> A clear, beginner-friendly official user guide students and parents can follow on their own</li>
              </ul>

              <h3 style="margin-top: 2rem;">What About Zorin OS?</h3>
              <p style="margin-top: 1rem;">We previously used <a href="https://zorin.com/os/education/" target="_blank" rel="noopener noreferrer" style="color: var(--secondary-color); text-decoration: underline;">Zorin OS</a>, and a number of students still have laptops running it. Those machines are <strong>still fully supported</strong> &mdash; you don't need to change anything, and we keep the Zorin OS guides on this site up to date. We standardized on Linux Mint Xfce going forward because it runs lighter on the older hardware we refurbish and has a larger pool of community documentation for students to lean on.</p>

              <div class="info-box info" style="margin-top: 2rem;">
                <strong>What About Windows or Mac?</strong> Linux provides the best experience for most students, combining professional capabilities with excellent performance. However, if you have specific software requirements that require Windows or macOS, we're happy to discuss alternatives. Just <a href="/contact" style="color: inherit; text-decoration: underline;">contact us</a> with your specific needs.
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Gaming Modal -->
          <div v-if="activeModal === 'gaming'">
            <h2>Gaming on Linux</h2>
            <p style="margin-top: 1rem;">While these laptops are primarily for school work, we believe students should be able to explore their interests and passions. Many people get into computer science through an interest in games—whether playing, developing, or modding them. Gaming can be a gateway to learning programming, 3D modeling, game design, and much more!</p>

            <div style="margin-top: 2rem;">
              <h3>🎮 Playing Games on Linux</h3>
              <p style="margin-top: 1rem;">Linux has come a long way for gaming. Here are two great gaming options you can install on your laptop:</p>

              <div class="gaming-section">
                <h4 style="color: var(--primary-color, #2c4a6b); margin-top: 1.5rem;">Sober - Play Roblox on Linux</h4>
                <p style="margin-top: 0.5rem;">Sober is a runtime that lets you play Roblox on Linux. It's easy to install using Flatpak!</p>

                <div class="info-box info" style="margin-top: 1rem;">
                  <strong>How to Install Sober:</strong>
                  <ol style="margin-top: 0.5rem;">
                    <li>Open the <strong>Terminal</strong> application (search for it in the menu)</li>
                    <li>Copy and paste this command and press Enter:</li>
                  </ol>
                  <code style="display: block; margin-top: 0.5rem; padding: 0.75rem; background: rgba(0, 0, 0, 0.1); border-radius: 4px; font-size: 0.95rem;">flatpak install flathub org.vinegarhq.Sober</code>
                  <ol start="3" style="margin-top: 0.5rem;">
                    <li>Type <code>y</code> and press Enter when asked to confirm</li>
                    <li>Wait for the installation to complete</li>
                    <li>Once installed, you can find Sober in your application menu!</li>
                  </ol>
                </div>

                <h4 style="color: var(--primary-color, #2c4a6b); margin-top: 2rem;">Luanti (Minetest) - Open Source Voxel Game</h4>
                <p style="margin-top: 0.5rem;">Luanti (formerly known as Minetest) is a free and open-source voxel game engine similar to Minecraft. It supports user-created games, mods, and has an active community!</p>

                <div class="info-box info" style="margin-top: 1rem;">
                  <strong>How to Install Luanti:</strong>
                  <ol style="margin-top: 0.5rem;">
                    <li>Open the <strong>Software Manager</strong> (Linux Mint) or <strong>Software Center</strong> (Zorin OS) from your menu</li>
                    <li>Search for <strong>"Minetest"</strong> or <strong>"Luanti"</strong></li>
                    <li>Click <strong>Install</strong></li>
                    <li>Enter your password when prompted</li>
                    <li>Launch from your application menu and start playing!</li>
                  </ol>
                </div>

                <p style="margin-top: 1rem; font-style: italic; color: #666;">
                  <strong>Why Luanti?</strong> It's completely free, runs great on older hardware, supports thousands of community-made mods and games, and is an excellent introduction to game modding and development!
                </p>
              </div>

              <h3 style="margin-top: 2.5rem;">🛠️ For Advanced Users: Build Luanti from Source</h3>
              <p style="margin-top: 1rem;">Ready for a challenge? Building software from source code is an important skill in software development. Here's how to build Luanti yourself and even modify the game!</p>

              <div class="info-box warning" style="margin-top: 1rem;">
                <strong>⚠️ Advanced Section:</strong> This section is for students comfortable with the command line and interested in learning software development. Don't worry if this seems complicated—it's meant to be a learning challenge!
              </div>

              <div style="margin-top: 1.5rem;">
                <h4 style="color: var(--secondary-color, #3ba3d1);">Step 1: Install Build Dependencies</h4>
                <p style="margin-top: 0.5rem;">First, we need to install the tools required to build Luanti:</p>
                <code style="display: block; margin-top: 0.5rem; padding: 0.75rem; background: rgba(0, 0, 0, 0.1); border-radius: 4px; font-size: 0.9rem;">sudo apt update && sudo apt install -y git build-essential cmake libgmp-dev libjpeg-dev libpng-dev libfreetype6-dev libsqlite3-dev libogg-dev libvorbis-dev libopenal-dev libcurl4-gnutls-dev libluajit-5.1-dev libleveldb-dev libzstd-dev gettext</code>

                <h4 style="color: var(--secondary-color, #3ba3d1); margin-top: 1.5rem;">Step 2: Clone the Luanti Repository</h4>
                <p style="margin-top: 0.5rem;">Download the Luanti source code from GitHub:</p>
                <code style="display: block; margin-top: 0.5rem; padding: 0.75rem; background: rgba(0, 0, 0, 0.1); border-radius: 4px; font-size: 0.9rem;">git clone --depth 1 https://github.com/minetest/minetest.git<br>cd minetest</code>

                <h4 style="color: var(--secondary-color, #3ba3d1); margin-top: 1.5rem;">Step 3: Build the Game</h4>
                <p style="margin-top: 0.5rem;">Compile the source code into a working game:</p>
                <code style="display: block; margin-top: 0.5rem; padding: 0.75rem; background: rgba(0, 0, 0, 0.1); border-radius: 4px; font-size: 0.9rem;">cmake . -DBUILD_CLIENT=1 -DBUILD_SERVER=0<br>make -j$(nproc)</code>
                <p style="margin-top: 0.5rem; font-size: 0.9rem; font-style: italic;">This will take several minutes. The <code>-j$(nproc)</code> flag uses all your CPU cores to build faster!</p>

                <h4 style="color: var(--secondary-color, #3ba3d1); margin-top: 1.5rem;">Step 4: Run Your Build</h4>
                <p style="margin-top: 0.5rem;">Start the game you just compiled:</p>
                <code style="display: block; margin-top: 0.5rem; padding: 0.75rem; background: rgba(0, 0, 0, 0.1); border-radius: 4px; font-size: 0.9rem;">./bin/minetest</code>
              </div>

              <h3 style="margin-top: 2.5rem;">🎯 Challenges for Advanced Users</h3>
              <p style="margin-top: 1rem;">Now that you've built Luanti, try these coding challenges to modify the game:</p>

              <div class="challenge-list" style="margin-top: 1rem;">
                <div class="challenge-item" style="background: #f8f9fa; padding: 1rem; border-radius: 6px; margin-bottom: 1rem; border-left: 4px solid var(--secondary-color, #3ba3d1);">
                  <h4 style="margin: 0 0 0.5rem 0; color: var(--primary-color, #2c4a6b);">Challenge 1: Change the Window Title</h4>
                  <p style="margin: 0; font-size: 0.95rem;">Find and modify the code that sets the game window title. Make it display your name or a custom message!</p>
                  <p style="margin: 0.5rem 0 0 0; font-size: 0.85rem; font-style: italic; color: #666;">Hint: Look in the <code>src/client/clientlauncher.cpp</code> file</p>
                </div>

                <div class="challenge-item" style="background: #f8f9fa; padding: 1rem; border-radius: 6px; margin-bottom: 1rem; border-left: 4px solid var(--secondary-color, #3ba3d1);">
                  <h4 style="margin: 0 0 0.5rem 0; color: var(--primary-color, #2c4a6b);">Challenge 2: Modify Player Speed</h4>
                  <p style="margin: 0; font-size: 0.95rem;">Find the player movement code and increase the walking speed by 50%. How does this change the gameplay?</p>
                  <p style="margin: 0.5rem 0 0 0; font-size: 0.85rem; font-style: italic; color: #666;">Hint: Search for movement-related constants in <code>src/</code> directory</p>
                </div>

                <div class="challenge-item" style="background: #f8f9fa; padding: 1rem; border-radius: 6px; margin-bottom: 1rem; border-left: 4px solid var(--secondary-color, #3ba3d1);">
                  <h4 style="margin: 0 0 0.5rem 0; color: var(--primary-color, #2c4a6b);">Challenge 3: Create a Custom Block Type</h4>
                  <p style="margin: 0; font-size: 0.95rem;">Add a completely new block type to the game with unique properties. Research how blocks are defined in the Luanti source code!</p>
                  <p style="margin: 0.5rem 0 0 0; font-size: 0.85rem; font-style: italic; color: #666;">Hint: Start by examining the built-in games in the <code>games/</code> directory</p>
                </div>

                <div class="challenge-item" style="background: #f8f9fa; padding: 1rem; border-radius: 6px; border-left: 4px solid var(--secondary-color, #3ba3d1);">
                  <h4 style="margin: 0 0 0.5rem 0; color: var(--primary-color, #2c4a6b);">Challenge 4: Add Debug Messages</h4>
                  <p style="margin: 0; font-size: 0.95rem;">Add console debug output that prints your position every second. This teaches you about the game loop and debugging!</p>
                  <p style="margin: 0.5rem 0 0 0; font-size: 0.85rem; font-style: italic; color: #666;">Hint: Use <code>infostream</code> or <code>errorstream</code> for console output</p>
                </div>
              </div>

              <div class="info-box success" style="margin-top: 2rem;">
                <strong>🌟 Learning Outcomes:</strong> By building and modifying Luanti, you'll learn:
                <ul style="margin-top: 0.5rem; margin-bottom: 0;">
                  <li>How to use Git and GitHub for source code management</li>
                  <li>The C++ programming language and CMake build system</li>
                  <li>How game engines work under the hood</li>
                  <li>Reading and navigating large codebases</li>
                  <li>The software development workflow used by professionals</li>
                </ul>
              </div>

              <div class="info-box info" style="margin-top: 1rem;">
                <strong>📖 Resources:</strong>
                <ul style="margin-top: 0.5rem; margin-bottom: 0;">
                  <li><a href="https://github.com/minetest/minetest" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">Luanti GitHub Repository</a></li>
                  <li><a href="https://wiki.minetest.net/Main_Page" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">Luanti Wiki & Modding Guides</a></li>
                  <li><a href="https://dev.luanti.org/" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">Luanti Developer Documentation</a></li>
                </ul>
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>

          <!-- Other Questions Modal -->
          <div v-if="activeModal === 'otherQuestions'">
            <h2>Additional Resources</h2>
            <p style="margin-top: 1rem;">Can't find what you're looking for? We have additional resources available!</p>

            <div style="margin-top: 2rem;">
              <h3>🌐 Official Zorin OS Help Center</h3>
              <p style="margin-top: 1rem;">The official Zorin OS documentation provides comprehensive guides, tutorials, and troubleshooting help for all aspects of using Zorin OS.</p>

              <div class="info-box info" style="margin-top: 1.5rem;">
                <strong>Topics covered include:</strong>
                <ul style="margin-top: 0.5rem; margin-bottom: 0;">
                  <li>Getting started with Zorin OS</li>
                  <li>Customizing your desktop</li>
                  <li>Installing and managing software</li>
                  <li>System settings and preferences</li>
                  <li>Troubleshooting common issues</li>
                  <li>Advanced features and tips</li>
                </ul>
              </div>

              <div style="margin-top: 1.5rem; text-align: center;">
                <button @click="openExternal('https://help.zorin.com')" class="btn btn-primary" style="font-size: 1.1rem;">
                  Visit Zorin Help Center
                  <span style="margin-left: 0.5rem;">→</span>
                </button>
              </div>

              <h3 style="margin-top: 2.5rem;">📧 Contact Space Coast STEM</h3>
              <p style="margin-top: 1rem;">If you still need help or have questions specific to your Space Coast STEM laptop:</p>

              <div class="info-box warning" style="margin-top: 1rem;">
                <ul style="margin: 0;">
                  <li><strong>Email:</strong> <a href="mailto:info@spacecoaststem.org" style="color: inherit; text-decoration: underline;">info@spacecoaststem.org</a></li>
                  <li><strong>Subject Line:</strong> Include "Tech Support" in your email subject</li>
                  <li><strong>Include:</strong> Your name, school, and a description of the issue</li>
                </ul>
              </div>
            </div>

            <button @click="closeModal" class="btn btn-primary" style="margin-top: 2rem;">Close</button>
          </div>
        </div>
      </div>
    </Teleport>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SupportCard from '@/components/SupportCard.vue'

const route = useRoute()
const router = useRouter()

const currentSection = ref<'general' | 'mint' | 'zorin'>('general')
const activeModal = ref<string | null>(null)

const openModal = (modalName: string) => {
  activeModal.value = modalName
  document.body.style.overflow = 'hidden'
  updateURL()
}

const closeModal = () => {
  activeModal.value = null
  document.body.style.overflow = 'auto'
  updateURL()
}

const openExternal = (url: string) => {
  window.open(url, '_blank', 'noopener noreferrer')
}

// Update URL with query params
const updateURL = () => {
  const query: any = {}

  if (currentSection.value !== 'general') {
    query.section = currentSection.value
  }

  if (activeModal.value) {
    query.topic = activeModal.value
  }

  // Only update if query params changed
  if (JSON.stringify(route.query) !== JSON.stringify(query)) {
    router.replace({ query })
  }
}

// Watch for section changes
watch(currentSection, () => {
  updateURL()
})

// Initialize from URL params
const initializeFromURL = () => {
  // Read section from URL
  if (route.query.section) {
    const section = route.query.section as string
    if (section === 'general' || section === 'mint' || section === 'zorin') {
      currentSection.value = section
    }
  }

  // Read topic from URL
  if (route.query.topic) {
    const topic = route.query.topic as string
    activeModal.value = topic
    document.body.style.overflow = 'hidden'
  }
}

// Close modal on Escape key
const handleEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    closeModal()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  initializeFromURL()
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.body.style.overflow = 'auto'
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
}

/* Content Section */
.content-section {
  padding: 5rem 2rem;
}

.content-section h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section h3 {
  font-size: 1.8rem;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: var(--secondary-color, #3ba3d1);
  font-family: 'Poppins', sans-serif;
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border: 2px solid transparent;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
}

.btn-primary {
  background: #e8f0f7;
  color: var(--primary-color, #2c4a6b);
  border: 2px solid var(--primary-color, #2c4a6b);
}

.btn-primary:hover {
  background: #d0e3f2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 74, 107, 0.2);
}

.btn-primary.active {
  background: var(--primary-color, #2c4a6b);
  color: var(--white, #ffffff);
  box-shadow: 0 4px 15px rgba(44, 74, 107, 0.3);
  border-color: var(--primary-color, #2c4a6b);
}

.btn-secondary {
  background: #e8f0f7;
  color: var(--primary-color, #2c4a6b);
  border: 2px solid var(--primary-color, #2c4a6b);
}

.btn-secondary:hover {
  background: #d0e3f2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 74, 107, 0.2);
}

.btn-secondary.active {
  background: var(--primary-color, #2c4a6b);
  color: var(--white, #ffffff);
  box-shadow: 0 4px 15px rgba(44, 74, 107, 0.3);
  border-color: var(--primary-color, #2c4a6b);
}

/* Program Cards Grid */
.program-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

/* Help Box */
.help-box {
  margin-top: 3rem;
  padding: 2rem;
  background: var(--light-bg, #f5f7fa);
  border-radius: 8px;
}

.help-box h3 {
  margin-top: 0;
  font-family: 'Poppins', sans-serif;
}

.help-box a {
  color: var(--secondary-color, #3ba3d1);
  text-decoration: none;
}

.help-box a:hover {
  text-decoration: underline;
}

/* Modal System */
.modal-overlay {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  overflow-y: auto;
  padding: 50px 20px;
}

.modal-content {
  background: white;
  padding: 2rem;
  max-width: 900px;
  width: 100%;
  border-radius: 8px;
  position: relative;
  margin: auto;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: #666;
  line-height: 1;
  padding: 0;
  width: 2rem;
  height: 2rem;
}

.modal-close:hover {
  color: #333;
}

.modal-content h2 {
  font-family: 'Poppins', sans-serif;
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1rem;
}

.modal-content h3 {
  font-family: 'Poppins', sans-serif;
  color: var(--secondary-color, #3ba3d1);
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.modal-content h4 {
  font-family: 'Poppins', sans-serif;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.modal-content ol, .modal-content ul {
  margin-left: 1.5rem;
  line-height: 1.8;
}

.modal-content li {
  margin-bottom: 0.5rem;
}

.modal-content code {
  background: rgba(0, 0, 0, 0.1);
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

/* Info Boxes */
.info-box {
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
}

.info-box.warning {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
}

.info-box.info {
  background: #d1ecf1;
  border-left: 4px solid #0c5460;
}

.info-box.success {
  background: #d4edda;
  border-left: 4px solid #28a745;
}

/* Rating Boxes */
.rating-box {
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
}

.rating-box h4 {
  margin: 0 0 0.5rem 0;
}

.rating-box p {
  margin: 0.5rem 0 0 0;
}

.rating-box.basic {
  background: #fff3cd;
  border-left: 4px solid #ffc107;
}

.rating-box.standard {
  background: #d4edda;
  border-left: 4px solid #4CAF50;
}

.rating-box.high {
  background: #d1ecf1;
  border-left: 4px solid #2196F3;
}

.rating-visual {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(to right, #ffc107 0%, #4CAF50 50%, #2196F3 100%);
  border-radius: 8px;
  min-height: 3rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero-content p {
    font-size: 1.1rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .content-section h2 {
    font-size: 2rem;
  }

  .program-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    padding: 1.5rem;
  }
}

/* Package Grid */
.package-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.package-card {
  background: var(--white, #ffffff);
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.package-card:hover {
  border-color: var(--secondary-color, #3ba3d1);
  transform: translateY(-4px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.package-logo {
  width: 64px;
  height: 64px;
  object-fit: contain;
  margin: 0 auto 1rem;
  display: block;
}

.package-card h4 {
  font-family: 'Poppins', sans-serif;
  color: var(--primary-color, #2c4a6b);
  margin: 0.5rem 0;
  font-size: 1.1rem;
}

.package-card p {
  color: var(--text-light, #666666);
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .package-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1rem;
  }

  .package-card {
    padding: 1rem;
  }

  .package-logo {
    width: 48px;
    height: 48px;
  }
}
</style>
